import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Button from '../components/ui/Button';
import { formatIdrFromUsd, toDisplayAmount } from '../utils/currency';

const CheckoutSuccess = () => {
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const state = location.state || {};
    
    // Handle new payment methods (CASH_IN_STORE, TEST_CARD)
    if (state.paymentMethod === 'CASH_IN_STORE' || state.paymentMethod === 'TEST_CARD') {
      setPaymentDetails(state);
      setIsLoading(false);
      return;
    }

    // Legacy Xendit payment status check
    const checkPaymentStatus = async () => {
      const params = new URLSearchParams(location.search);
      const paymentId = params.get('payment_id') || state.paymentId;
      const orderId = params.get('order_id') || state.orderId;

      if (!paymentId && !orderId) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`/api/xendit/payment-status?${paymentId ? 'paymentId=' + paymentId : 'orderId=' + orderId}`);
        const data = await response.json();
        
        if (data.success) {
          setPaymentDetails(data.payment);
        } else {
          console.error('Failed to fetch payment status:', data.error);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
      } finally {
        setIsLoading(false);
      }
    };

    checkPaymentStatus();
  }, [location.search, location.state]);

  if (isLoading) {
    return (
      <Layout>
        <div className="max-w-md mx-auto mt-16 p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Проверяем статус платежа...</p>
        </div>
      </Layout>
    );
  }

  // Render Cash Payment Success Page
  if (paymentDetails?.paymentMethod === 'CASH_IN_STORE') {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-amber-500/20 mb-4">
                <span className="text-3xl">💰</span>
              </div>
              <h1 className="font-serif text-3xl font-bold text-primary mb-4">
                Order Created - Cash Payment Required
              </h1>
              <p className="text-gray-400">
                Your order #{paymentDetails.orderNumber} has been created and is awaiting prepayment.
              </p>
            </div>

            <div className="bg-secondary border border-gray-800 rounded-lg p-6 mb-6">
              <h2 className="font-serif text-xl font-bold text-primary mb-4">📍 Workshop Visit Required</h2>
              
              <div className="space-y-4">
                <div className="bg-primary/10 rounded-lg p-4 border border-primary/20">
                  <h3 className="text-primary font-semibold mb-2">KALA HATS Workshop</h3>
                  <p className="text-gray-300 text-sm mb-2">
                    Jl. Monkey Forest Road, Ubud<br />
                    Gianyar, Bali 80571, Indonesia
                  </p>
                  <p className="text-gray-400 text-xs">
                    <strong>Hours:</strong> Mon-Sat 9AM-6PM, Sun 10AM-4PM<br />
                    <strong>Phone:</strong> +62 361 XXX-XXXX
                  </p>
                </div>

                <div className="bg-amber-500/10 rounded-lg p-4 border border-amber-500/20">
                  <h3 className="text-amber-400 font-semibold mb-2">⚡ Next Steps</h3>
                    <ol className="text-gray-300 text-sm space-y-1">
                      <li>1. Visit our workshop within 7 days</li>
                      <li>2. Bring valid ID and order number</li>
                      <li>3. Pay full amount: <span className="font-semibold text-primary">{formatIdrFromUsd(paymentDetails.total)}</span></li>
                      <li>4. We'll craft your custom hat (5-10 days)</li>
                      <li>5. Return to collect your finished piece</li>
                    </ol>
                </div>

                <div className="bg-red-500/10 rounded-lg p-4 border border-red-500/20">
                  <p className="text-red-400 text-sm">
                    <strong>Important:</strong> Order will be cancelled if prepayment is not received within 7 days.
                  </p>
                </div>
              </div>
            </div>

            <div className="text-center space-y-4">
              <Button onClick={() => navigate('/products')} variant="primary">
                Continue Shopping
              </Button>
              <Button onClick={() => navigate('/')} variant="secondary">
                Return Home
              </Button>
            </div>

            <div className="mt-8 text-center">
              <p className="text-primary italic">
                "Meet the craftsman behind your rebellion. Witness the birth of your artifact."
              </p>
              <p className="text-gray-500 text-sm mt-2">— SASTRAJIWA & the Outlaw Souls</p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Render Test Payment Success Page
  if (paymentDetails?.paymentMethod === 'TEST_CARD') {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-16">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-500/20 mb-4">
                <svg className="h-8 w-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                </svg>
              </div>
              <h1 className="font-serif text-3xl font-bold text-primary mb-4">
                Test Payment Successful!
              </h1>
              <p className="text-gray-400">
                Order #{paymentDetails.orderNumber} has been processed successfully.
              </p>
            </div>

            <div className="bg-secondary border border-gray-800 rounded-lg p-6 mb-6">
              <h2 className="font-serif text-xl font-bold text-primary mb-4">Order Details</h2>
              
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-400">Order Number:</span>
                  <span className="text-light font-medium">{paymentDetails.orderNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Payment Method:</span>
                  <span className="text-light">Test Card Payment</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Total Amount:</span>
                  <span className="text-primary font-semibold">{formatIdrFromUsd(paymentDetails.total)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Status:</span>
                  <span className="text-green-400 font-medium">✓ Paid</span>
                </div>
              </div>

              {process.env.NODE_ENV === 'development' && (
                <div className="mt-4 p-3 bg-yellow-500/10 rounded-lg border border-yellow-500/20">
                  <p className="text-yellow-400 text-sm">
                    <strong>Development Mode:</strong> This was a test payment. No actual charges were made.
                  </p>
                </div>
              )}
            </div>

            <div className="text-center space-y-4">
              <Button onClick={() => navigate('/products')} variant="primary">
                Continue Shopping
              </Button>
              <Button onClick={() => navigate('/')} variant="secondary">
                Return Home
              </Button>
            </div>

            <div className="mt-8 p-4 bg-primary/10 rounded-lg border border-primary/20 text-center">
              <p className="text-primary text-sm">
                📧 Order confirmation will be sent to your email shortly.
              </p>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  // Legacy/Standard Payment Success Page
  return (
    <Layout>
      <div className="container mx-auto px-4 py-16">
        <div className="max-w-2xl mx-auto">
          <div className="text-center mb-8">
            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-500/20 mb-4">
              <svg className="h-8 w-8 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
              </svg>
            </div>
            <h1 className="font-serif text-3xl font-bold text-primary mb-4">
              Payment Successful!
            </h1>
            <p className="text-gray-400 mb-6">
              Thank you for your purchase. Your order is being processed.
            </p>
          </div>
          
          {!paymentDetails && (
            <div className="bg-secondary border border-gray-800 rounded-lg p-6 mb-6">
              <h3 className="font-semibold text-primary mb-2">Payment Confirmed</h3>
              <p className="text-gray-400 text-sm">
                We couldn't load payment details, but this doesn't affect the success of your payment. 
                You'll receive confirmation via email and can check your order status in order history.
              </p>
            </div>
          )}

          {paymentDetails && !paymentDetails.paymentMethod && (
            <div className="bg-secondary border border-gray-800 rounded-lg p-6 mb-6">
              <h3 className="font-serif text-xl font-bold text-primary mb-4">Payment Details</h3>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-400">Payment ID:</span>
                  <span className="text-light font-medium">{paymentDetails.id}</span>
                </div>
                {paymentDetails.reference_id && (
                  <div className="flex justify-between">
                    <span className="text-gray-400">Order ID:</span>
                    <span className="text-light font-medium">{paymentDetails.reference_id}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-400">Amount:</span>
                  <span className="text-primary font-semibold">
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: paymentDetails.currency || 'USD'
                    }).format(toDisplayAmount(paymentDetails.amount, paymentDetails.currency))}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Status:</span>
                  <span className="text-green-400 font-medium">✓ Paid</span>
                </div>
              </div>
            </div>
          )}

          <div className="text-center space-y-4">
            <Button onClick={() => navigate('/products')} variant="primary">
              Continue Shopping
            </Button>
            <Button onClick={() => navigate('/')} variant="secondary">
              Return Home
            </Button>
          </div>

          <div className="mt-8 p-4 bg-primary/10 rounded-lg border border-primary/20 text-center">
            <p className="text-primary text-sm">
              📧 Order confirmation will be sent to your email shortly.
            </p>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CheckoutSuccess;
